import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, X as XIcon, Sparkles, ArrowRight, Shield, Zap, FileText, Users, Loader2, UserCog, Bot, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const PricingPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 29,
      duration: '/mois',
      description: 'Pour independants et micro-entreprises',
      features: [
        'Factures & devis illimites',
        'Clients illimites',
        '20 modeles PDF professionnels',
        'QR-Facture suisse conforme SIX',
        'Multi-devises (CHF/EUR/USD/GBP)',
        'Envoi email avec PDF joint',
        'Bilan fiscal & suivi depenses',
        'Export comptable Bexio/Cresus',
        'Support email'
      ],
      popular: false
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 39,
      originalPrice: 49,
      duration: '/mois',
      description: 'Pour PME en croissance',
      savings: '20%',
      features: [
        'Tout du plan Starter +',
        'Module RH & Paie suisse complet',
        'Fiches de paie & certificats Form. 11',
        'Dashboard RH interactif',
        'Planning & absences (26 cantons)',
        'Ecritures comptables paie',
        'Multi-utilisateurs (equipe)',
        'Synchronisation Shopify',
        'Factures recurrentes',
        'Support prioritaire'
      ],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Tout-en-un',
      price: 549,
      originalPrice: 708,
      duration: '/an',
      description: 'Tout inclus, meilleur prix',
      savings: '23%',
      features: [
        'Tout du plan Pro +',
        'Economisez 159 CHF/an',
        'Assistant IA integre',
        'Gestion de stock & inventaire',
        'Portail client avec paiement en ligne',
        'Application mobile (PWA)',
        'Support dedie & onboarding',
        'Acces prioritaire nouvelles fonctionnalites'
      ],
      popular: false
    }
  ];

  // Module comparison data
  const moduleComparison = [
    {
      category: 'Facturation',
      features: [
        { name: 'Factures & devis illimites', starter: true, pro: true, enterprise: true },
        { name: '20 modeles PDF professionnels', starter: true, pro: true, enterprise: true },
        { name: 'QR-Facture suisse (SIX)', starter: true, pro: true, enterprise: true },
        { name: 'Multi-devises (CHF/EUR/USD/GBP)', starter: true, pro: true, enterprise: true },
        { name: 'Paiements partiels & avoirs', starter: true, pro: true, enterprise: true },
        { name: 'Envoi par email avec PDF', starter: true, pro: true, enterprise: true },
        { name: 'Factures recurrentes', starter: false, pro: true, enterprise: true },
      ]
    },
    {
      category: 'Clients & CRM',
      features: [
        { name: 'Clients illimites', starter: true, pro: true, enterprise: true },
        { name: 'Historique & suivi paiements', starter: true, pro: true, enterprise: true },
        { name: 'Import CSV en masse', starter: false, pro: false, enterprise: true },
      ]
    },
    {
      category: 'Comptabilite',
      features: [
        { name: 'Bilan fiscal & depenses', starter: true, pro: true, enterprise: true },
        { name: 'Export comptable (Bexio/Cresus)', starter: true, pro: true, enterprise: true },
        { name: 'Ecritures comptables paie', starter: false, pro: true, enterprise: true },
      ]
    },
    {
      category: 'RH & Paie suisse',
      features: [
        { name: 'Gestion des employes', starter: false, pro: true, enterprise: true },
        { name: 'Fiches de paie (AVS/LPP/AC)', starter: false, pro: true, enterprise: true },
        { name: 'Certificats de salaire Form. 11', starter: false, pro: true, enterprise: true },
        { name: 'Dashboard RH interactif', starter: false, pro: true, enterprise: true },
        { name: 'Planning & absences (26 cantons)', starter: false, pro: true, enterprise: true },
      ]
    },
    {
      category: 'E-commerce',
      features: [
        { name: 'Synchronisation Shopify', starter: false, pro: true, enterprise: true },
      ]
    },
    {
      category: 'Equipe & Collaboration',
      features: [
        { name: 'Multi-utilisateurs', starter: false, pro: true, enterprise: true },
        { name: 'Roles & permissions', starter: false, pro: true, enterprise: true },
      ]
    },
    {
      category: 'Intelligence Artificielle',
      features: [
        { name: 'Assistant IA integre', starter: false, pro: false, enterprise: true },
        { name: 'Onboarding guide', starter: true, pro: true, enterprise: true },
      ]
    },
    {
      category: 'Support',
      features: [
        { name: 'Support email', starter: true, pro: true, enterprise: true },
        { name: 'Support prioritaire', starter: false, pro: true, enterprise: true },
        { name: 'Support dedie & onboarding', starter: false, pro: false, enterprise: true },
      ]
    }
  ];

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      toast.info('Veuillez vous connecter ou creer un compte');
      navigate('/register');
      return;
    }
    if (user?.status === 'active') {
      toast.info('Vous avez deja un abonnement actif');
      return;
    }
    setLoading(planId);
    try {
      const response = await api.post('/payments/create-checkout', {
        package_id: planId,
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Erreur lors de la creation du paiement');
      setLoading(null);
    }
  };

  const highlights = [
    { icon: FileText, title: 'Facturation complete', desc: '20 modeles PDF + QR-facture' },
    { icon: Users, title: 'CRM & Clients', desc: 'Historique, suivi, import CSV' },
    { icon: UserCog, title: 'RH & Paie suisse', desc: 'AVS, LPP, AC, 26 cantons' },
    { icon: Bot, title: 'Assistant IA', desc: 'Guide integre pour vous aider' }
  ];

  const CheckCell = ({ included }) => (
    included
      ? <Check className="w-5 h-5 text-emerald-500 mx-auto" />
      : <XIcon className="w-4 h-4 text-slate-300 mx-auto" />
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/xyron-logo.png" alt="Xyron Core ERP" className="w-8 h-8 rounded-lg" />
            <h1 className="font-heading text-xl font-bold text-slate-900">
              Xyron<span className="text-blue-600">Core ERP</span>
            </h1>
          </div>
          <div className="flex gap-3">
            {isAuthenticated ? (
              <Button onClick={() => navigate('/app/dashboard')} variant="outline">Tableau de bord</Button>
            ) : (
              <>
                <Button onClick={() => navigate('/login')} variant="ghost">Connexion</Button>
                <Button onClick={() => navigate('/register')} className="bg-blue-600 hover:bg-blue-700">Essai gratuit</Button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100" data-testid="pricing-trial-badge">
            <Sparkles className="w-3 h-3 mr-1" />
            Essai gratuit : 3 factures + 3 devis
          </Badge>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Votre ERP suisse,<br />
            <span className="text-blue-600">par modules</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Choisissez le plan adapte a votre activite. Chaque module est inclus ou non selon votre pack.
          </p>
        </div>

        {/* Highlights */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {highlights.map((h, i) => (
            <div key={i} className="p-4 bg-white rounded-xl border border-slate-200 text-center">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mx-auto mb-3">
                <h.icon className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-medium text-slate-900 text-sm">{h.title}</h3>
              <p className="text-xs text-slate-500 mt-1">{h.desc}</p>
            </div>
          ))}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16" data-testid="pricing-cards">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative border-2 transition-all hover:shadow-lg ${
                plan.popular ? 'border-blue-500 shadow-blue-100 shadow-lg scale-105' : 'border-slate-200'
              }`}
              data-testid={`pricing-plan-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-blue-600 text-white hover:bg-blue-600">Le plus populaire</Badge>
                </div>
              )}
              <CardHeader className="text-center pb-2">
                <CardTitle className="font-heading text-xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-4">
                  {plan.originalPrice && (
                    <span className="text-lg text-slate-400 line-through mr-2">{plan.originalPrice} CHF</span>
                  )}
                  <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                  <span className="text-slate-500"> CHF{plan.duration}</span>
                  {plan.savings && (
                    <Badge className="ml-2 bg-green-100 text-green-700 hover:bg-green-100">-{plan.savings}</Badge>
                  )}
                </div>
                <ul className="space-y-3 text-left mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading === plan.id || user?.status === 'active'}
                  className={`w-full ${plan.popular ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-900 hover:bg-slate-800'}`}
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  {loading === plan.id ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />Chargement...</>
                  ) : user?.status === 'active' ? (
                    'Deja abonne'
                  ) : (
                    <>S'abonner<ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Module Comparison Table */}
        <div className="mb-16" data-testid="module-comparison">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-2">
              Comparatif detaille par modules
            </h2>
            <p className="text-slate-600">Decouvrez exactement ce qui est inclus dans chaque plan</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            {/* Table Header */}
            <div className="grid grid-cols-4 bg-slate-50 border-b border-slate-200 sticky top-[73px] z-10">
              <div className="p-4 font-semibold text-slate-700 text-sm">Module / Fonctionnalite</div>
              <div className="p-4 text-center">
                <p className="font-semibold text-slate-900 text-sm">Starter</p>
                <p className="text-xs text-slate-500">29 CHF/mois</p>
              </div>
              <div className="p-4 text-center bg-blue-50/50">
                <p className="font-semibold text-blue-700 text-sm">Pro</p>
                <p className="text-xs text-blue-500">59 CHF/mois</p>
              </div>
              <div className="p-4 text-center">
                <p className="font-semibold text-slate-900 text-sm">Tout-en-un</p>
                <p className="text-xs text-slate-500">549 CHF/an</p>
              </div>
            </div>

            {/* Table Body */}
            {moduleComparison.map((section, si) => (
              <div key={si}>
                {/* Category Header */}
                <div className="grid grid-cols-4 bg-slate-50/70 border-b border-slate-100">
                  <div className="p-3 pl-4 font-semibold text-slate-800 text-sm col-span-4">
                    {section.category}
                  </div>
                </div>
                {/* Features */}
                {section.features.map((feat, fi) => (
                  <div key={fi} className="grid grid-cols-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50 transition-colors">
                    <div className="p-3 pl-6 text-sm text-slate-600">{feat.name}</div>
                    <div className="p-3 flex items-center justify-center"><CheckCell included={feat.starter} /></div>
                    <div className="p-3 flex items-center justify-center bg-blue-50/20"><CheckCell included={feat.pro} /></div>
                    <div className="p-3 flex items-center justify-center"><CheckCell included={feat.enterprise} /></div>
                  </div>
                ))}
              </div>
            ))}

            {/* Table Footer - CTA */}
            <div className="grid grid-cols-4 bg-slate-50 border-t border-slate-200">
              <div className="p-4"></div>
              <div className="p-4 text-center">
                <Button size="sm" variant="outline" onClick={() => handleSubscribe('starter')} className="text-xs" data-testid="table-subscribe-starter">
                  Choisir Starter
                </Button>
              </div>
              <div className="p-4 text-center bg-blue-50/50">
                <Button size="sm" onClick={() => handleSubscribe('pro')} className="text-xs bg-blue-600 hover:bg-blue-700" data-testid="table-subscribe-pro">
                  Choisir Pro
                </Button>
              </div>
              <div className="p-4 text-center">
                <Button size="sm" variant="outline" onClick={() => handleSubscribe('enterprise')} className="text-xs" data-testid="table-subscribe-enterprise">
                  Choisir Tout-en-un
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Trial CTA */}
        {!isAuthenticated && (
          <div className="text-center p-8 bg-slate-900 rounded-2xl text-white mb-12">
            <h2 className="font-heading text-2xl font-bold mb-2">Pas encore convaincu ?</h2>
            <p className="text-slate-300 mb-4">Commencez gratuitement avec 3 factures et 3 devis. Sans carte bancaire.</p>
            <Button onClick={() => navigate('/register')} className="bg-white text-slate-900 hover:bg-slate-100" data-testid="trial-cta-btn">
              Creer mon compte gratuit<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* FAQ */}
        <div className="mb-12">
          <h2 className="font-heading text-2xl font-bold text-center mb-6">Questions frequentes</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-900 mb-2">Puis-je changer de plan ?</h3>
                <p className="text-sm text-slate-600">Oui, vous pouvez passer du Starter au Pro a tout moment. La difference sera calculee au prorata.</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-900 mb-2">Quels moyens de paiement ?</h3>
                <p className="text-sm text-slate-600">Visa, Mastercard, American Express via Stripe securise. Paiement en CHF.</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-900 mb-2">Mes donnees sont-elles securisees ?</h3>
                <p className="text-sm text-slate-600">Absolument. Chiffrement SSL, serveurs en Suisse, sauvegardes quotidiennes. Conforme RGPD et nLPD.</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <h3 className="font-medium text-slate-900 mb-2">Le plan Tout-en-un est-il annuel ?</h3>
                <p className="text-sm text-slate-600">Oui, le plan Tout-en-un est facture une fois par an (549 CHF) soit ~46 CHF/mois. C'est 23% moins cher que le Pro mensuel.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>&copy; 2026 Xyron Core ERP - ERP Suisse pour PME</p>
          <p className="mt-1">Paiements securises par Stripe</p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
